import { DecisionGraph, getNodeResult } from "@imminently/decisively-core";

/**
 * The graph views show a DecisionGraph, optionally narrowed to some of its nodes (e.g. those connected to the goal):
 * `visible` has a 1 for every id in the view, or is null for the whole graph. Nothing is copied or built to narrow
 * it, and node objects are only made for the nodes a view reads.
 */
export type VisibleMask = Uint8Array | null;

export const isShown = (visible: VisibleMask | undefined, id: number) => !visible || visible[id] === 1;

/** The id of a path (or global guid) when it's in the view. */
export const shownRef = (
  graph: DecisionGraph | null | undefined,
  visible: VisibleMask | undefined,
  path: string | null | undefined,
): number | undefined => {
  if (!graph || path === null || path === undefined) return undefined;
  const id = graph.ref(path);
  return id !== undefined && isShown(visible, id) ? id : undefined;
};

/** The node object of a path (or global guid) when it's in the view. */
export const shownNode = (
  graph: DecisionGraph | null | undefined,
  visible: VisibleMask | undefined,
  path: string | null | undefined,
): Record<string, any> | undefined => {
  const id = shownRef(graph, visible, path);
  return id === undefined ? undefined : graph!.node(id);
};

export const shownSuccessors = (graph: DecisionGraph, visible: VisibleMask | undefined, id: number): number[] => {
  const out: number[] = [];
  for (const to of graph.successors(id)) if (isShown(visible, to)) out.push(to);
  return out;
};

export const shownPredecessors = (graph: DecisionGraph, visible: VisibleMask | undefined, id: number): number[] => {
  const out: number[] = [];
  for (const from of graph.predecessors(id)) if (isShown(visible, from)) out.push(from);
  return out;
};

/** The listed nodes in the view, in id order. */
export function* shownIds(graph: DecisionGraph, visible: VisibleMask | undefined): Generator<number> {
  for (const id of graph.ids()) if (isShown(visible, id)) yield id;
}

/** A hidden node is the template shared by its instances: the one visible instance of it in the view, if only one. */
export const onlyVisibleInstance = (
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  guid: string,
): number | undefined => {
  let found: number | undefined;
  for (const id of graph.instancesOf(guid)) {
    if (!isShown(visible, id)) continue;
    if (found !== undefined) return undefined;
    found = id;
  }
  return found;
};

/** The first visible instance of a template in the view. */
export const firstVisibleInstance = (graph: DecisionGraph, visible: VisibleMask | undefined, guid: string) => {
  for (const id of graph.instancesOf(guid)) if (isShown(visible, id)) return id;
  return undefined;
};

/**
 * The debugger's view of a result: the nodes connected to the goal, plus nodes kept as references for explanations
 * (hidden templates, identifiers, validations).
 */
export const goalMask = (graph: DecisionGraph, goal: number): Uint8Array => {
  const visible = graph.reachable(goal);
  const identifiers = graph.templateField("identifier");
  const validations = graph.templateField("validation");
  for (let id = 0; id < graph.idBound(); id++) {
    if (visible[id]) continue;
    const t = graph.template(id);
    if (!graph.isListed(id) || graph.isHidden(id) || identifiers[t] || validations[t]) visible[id] = 1;
  }
  return visible;
};

/** The goal and its direct (non hidden) inputs. */
export const childrenMask = (graph: DecisionGraph, goal: number): Uint8Array => {
  const visible = new Uint8Array(graph.idBound());
  visible[goal] = 1;
  for (const to of graph.successors(goal)) if (!graph.field(to, "hidden")) visible[to] = 1;
  return visible;
};

/** Every node in the view except those `drop` returns true for (e.g. a release graph's foreign keys). */
export const maskWithout = (graph: DecisionGraph, drop: (id: number) => boolean): Uint8Array => {
  const visible = new Uint8Array(graph.idBound());
  for (let id = 0; id < graph.idBound(); id++) visible[id] = graph.isListed(id) && !drop(id) ? 1 : 0;
  return visible;
};

/**
 * An instance's index as views show it: the value of its identifier attribute when it has one (with the index as a
 * hint), otherwise the index.
 */
export const instanceIndexInfo = (
  graph: DecisionGraph | null | undefined,
  node: { entity?: string; index?: unknown; parent_path?: string; _indexData?: any } | null | undefined,
) => {
  if (!node) return undefined;
  if (node._indexData) return node._indexData;
  const index = node.index;
  if (index === undefined || !graph) return undefined;
  const instancePath = `${node.parent_path ? `${node.parent_path}/` : ""}${node.entity}/${index}`;
  const identifierId = graph.identifierNode(instancePath);
  const identifier = identifierId === undefined ? undefined : graph.node(identifierId);
  const value = identifier ? getNodeResult(identifier as any) : undefined;
  const info =
    identifier && value !== undefined
      ? { value, hint: index, identifier: identifier.description }
      : { value: index, identifier: identifier?.description };
  // the same node object is drawn again on re-render: keep the answer on it, as graphlib-era code did
  if (Object.isExtensible(node)) node._indexData = info;
  return info;
};

/**
 * At most `limit` of the view's nodes: all of them when they fit, otherwise those nearest the goal (breadth first
 * along both directions), topped up in id order.
 */
export const boundedView = (
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  goal: string | null | undefined,
  limit: number,
): number[] => {
  const ids: number[] = [];
  const taken = new Uint8Array(graph.idBound());
  const take = (id: number) => {
    if (taken[id] || !graph.isListed(id) || !isShown(visible, id)) return;
    taken[id] = 1;
    ids.push(id);
  };
  const start = shownRef(graph, visible, goal ?? undefined);
  if (start !== undefined) {
    take(start);
    for (let head = 0; head < ids.length && ids.length < limit; head++) {
      const id = ids[head];
      for (const to of graph.successors(id)) if (ids.length < limit) take(to);
      for (const from of graph.predecessors(id)) if (ids.length < limit) take(from);
    }
  }
  for (const id of graph.ids()) {
    if (ids.length >= limit) break;
    take(id);
  }
  return ids;
};

/** The root, the nodes within `depth` steps of it either way, and `additional`: what a restricted view can draw. */
export const restrictedViewIds = (
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  rootPath: string,
  depth: number,
  additional: string[] = [],
): number[] => {
  const ids = new Set<number>();
  const root = shownRef(graph, visible, rootPath);
  if (root !== undefined) {
    const include = (id: number) => isShown(visible, id);
    ids.add(root);
    for (const id of graph.neighbourhood(root, depth, "predecessors", include)) ids.add(id);
    for (const id of graph.neighbourhood(root, depth, "successors", include)) ids.add(id);
  }
  for (const path of additional) {
    const id = shownRef(graph, visible, path);
    if (id !== undefined) ids.add(id);
  }
  return [...ids];
};

export type LimitedView = {
  /** The nodes the renderer may draw. */
  ids: number[];
  /** When the view is too big to draw whole: where to start, and the nodes around the goals that fit the limit. */
  restriction?: { rootPath: string; graphDepth: number; additionalVisibleNodes: string[] };
};

/**
 * What to draw of an unrestricted view: all of it when it fits in `limit` nodes; otherwise the goals (the goal first,
 * then nodes nothing depends on, then the rest) each expanded outwards one step at a time, while it fits.
 */
export const limitedView = (
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  { limit, showHidden, goal }: { limit: number; showHidden?: boolean; goal?: string | null },
): LimitedView => {
  const drawable = (id: number) => isShown(visible, id) && (showHidden || !graph.field(id, "hidden"));
  const all: number[] = [];
  for (const id of graph.ids()) {
    if (all.length > limit) break;
    if (drawable(id)) all.push(id);
  }
  if (all.length <= limit) return { ids: all };

  const fks = graph.templateField("fk");
  const identifiers = graph.templateField("identifier");
  const candidate = (id: number) => drawable(id) && !fks[graph.template(id)] && !identifiers[graph.template(id)];
  const isGoal = (id: number) => shownPredecessors(graph, visible, id).length === 0;
  const goalId = shownRef(graph, visible, goal ?? undefined);
  function* ordered() {
    if (goalId !== undefined && candidate(goalId)) yield goalId;
    for (const id of graph.ids()) if (id !== goalId && candidate(id) && isGoal(id)) yield id;
    for (const id of graph.ids()) if (id !== goalId && candidate(id) && !isGoal(id)) yield id;
  }

  const include = (id: number) => isShown(visible, id);
  const chosen = new Set<number>();
  let root: number | undefined;
  for (const start of ordered()) {
    if (root === undefined) root = start;
    if (chosen.size >= limit) break;
    let expanded: number[] = [];
    for (let depth = 1; depth <= 25; depth++) {
      const reached = [
        start,
        ...graph.neighbourhood(start, depth, "predecessors", include),
        ...graph.neighbourhood(start, depth, "successors", include),
      ];
      if (reached.length === expanded.length || reached.length + chosen.size > limit) break;
      expanded = reached;
    }
    for (const id of expanded) chosen.add(id);
  }
  if (root === undefined) return { ids: all.slice(0, limit) };
  const rootPath = graph.deref(root);
  const additionalVisibleNodes = [...chosen].map((id) => graph.deref(id));
  return {
    ids: restrictedViewIds(graph, visible, rootPath, 1, additionalVisibleNodes),
    restriction: { rootPath, graphDepth: 1, additionalVisibleNodes },
  };
};

/** The paths of a node's neighbours in the view (optionally leaving out hidden ones), for a drawn node. */
export const neighbourPaths = (
  graph: DecisionGraph,
  visible: VisibleMask | undefined,
  path: string,
  direction: "successors" | "predecessors",
  { skipHidden = false }: { skipHidden?: boolean } = {},
): string[] => {
  const id = shownRef(graph, visible, path);
  if (id === undefined) return [];
  const ids = direction === "successors" ? shownSuccessors(graph, visible, id) : shownPredecessors(graph, visible, id);
  return ids.filter((to) => !skipHidden || !graph.field(to, "hidden")).map((to) => graph.deref(to));
};

/** A node by key (path or guid), from a graph in any form (a session's decision graph, a release's rule graph). */
export const graphNodeByKey = (graph: any, key: string | null | undefined): Record<string, any> | undefined => {
  if (!graph || key === null || key === undefined) return undefined;
  const g = DecisionGraph.from(graph);
  const id = g.ref(key);
  return id === undefined ? undefined : g.node(id);
};
